import { useEffect, useState, useRef } from 'react';
import {
    Grid,
    Flex,
    Heading,
    Text,
    Image,
    Box,
    Link as ThemeLink
} from '@theme-ui/components';
import {
    Pagination,
    Table,
    Tooltip
} from '~/Common';
import { Link } from 'react-router-dom';
import { Icon } from 'assets/Icon';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    getPortfolioListedDealsAsync,
    getPortfolioUnlistedDealsAsync,
    getPortfolioDeals,
    getPortfolioEquityHoldingsAsync
} from 'features/portfolio/portfolioSlice';
import {
    Circle,
    ImagePlaceholder
} from '~/Common'
import { StatusIcon } from '~/Investments'
import {
    formatCurrency
} from 'utils/helpers';
import { useResponsiveValue } from '@theme-ui/match-media'
import { DealInvestmentStatus } from 'app/constants';

const getAcceptanceDoc = investment => {
    switch (investment.status.id) {
        case DealInvestmentStatus.agreement_signed:
        case DealInvestmentStatus.money_received:
        case DealInvestmentStatus.fulfilled:
            return investment.signed_document ? {
                url: investment.signed_document,
                label: 'Signed'
            } : false;
        case DealInvestmentStatus.authorised:
            return investment.acceptance_document ? {
                url: investment.acceptance_document,
                label: 'Acceptance'
            } : false;
        default:
            return false;
    }
}

const getRowLink = (investment, props) => {
    const requiresAccept = investment?.status?.id === DealInvestmentStatus.authorised;
    const linkTo = requiresAccept ? {
        as: ThemeLink,
        href: investment.acceptance_url,
        className: 'btn'
    } : {
        as: Link,
        to: `/deals/${investment?.deal?.slug}`
    };

    return (
        <ThemeLink
            {...linkTo}
            className="btn"
            sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 60
            }}
            {...props}
        />
    )
}

export const InvestmentsList = ({ portfolio, ...props }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const headingRef = useRef(null);
    const [isLoading, setLoading] = useState(true);
    const smoothScroll = (ref) => {
        window.scrollTo({ behavior: 'smooth', top: ref.current.offsetTop });
    };
    const dealsToShow = portfolio?.deals?.listed;
    const activePage = portfolio?.deals?.listed?.meta?.current_page;
    const pageCount = portfolio?.deals?.listed?.meta?.last_page;
    const isTablet = useResponsiveValue([true, true, true, false]);
    const isMobile = useResponsiveValue([true, false]);

    useEffect(() => {
        if(portfolio?.deals?.listed?.status === 'idle'){
            setLoading(true);
            dispatch(getPortfolioListedDealsAsync());
            dispatch(getPortfolioUnlistedDealsAsync());
            dispatch(getPortfolioEquityHoldingsAsync());
        }
        if(portfolio?.deals?.listed?.status === 'attempted'){
            setLoading(false);
        }
    }, [portfolio, setLoading, dispatch]);
    
    var columns = [
        {
            key: 'company',
            title: (
                <Flex
                    sx={{
                        pr: '4px',
                        flex: 1,
                        justifyContent: 'space-between'
                    }}
                >
                    <Box>{t('Company')}</Box>
                    <Box>{t('Status')}</Box>
                </Flex>
            ),
            width: !isTablet ? '500px' : '400px',
            fixed: !isMobile,
            sx: { zIndex: 65 },
            render: investment => {
                const acceptanceDoc = getAcceptanceDoc(investment);

                return (
                    <Grid sx={{
                        gridTemplate: 'auto / 40px auto 50px',
                        alignItems: 'center',
                        width: '100%',
                        pr: '17px'
                    }}>
                        <Circle size="40px" type="box" sx={{
                            boxShadow: 'none',
                            bg: 'transparent'
                        }}>
                            {investment?.deal?.company?.logo?.sizes?.tiny ? (
                                <Image src={investment?.deal?.company?.logo?.sizes?.tiny} sx={{ borderRadius: '50%' }} />
                            ) : (
                                <ImagePlaceholder sx={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%'
                                }} />
                            )}
                        </Circle>
                        <Box pb={0} sx={{
                            position: 'relative',
                            alignItems: 'center',
                            order: [2,2,1]
                        }}>
                            <Heading as="h3" variant="h5" sx={{ whiteSpace: 'normal' }}>
                                {t(investment?.deal.name)}
                            </Heading>
                            <Text as="p" variant="tag" color="dark" sx={{ fontSize: 0, fontWeight: 500 }}>{investment?.deal.asx ? `ASX:${investment?.deal.asx}` : investment.deal.listed_on_alternate_exchange ? `${investment.deal.alternate_exchange}:${investment.deal.alternate_security_code}` : 'Unlisted'}</Text>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'top',
                            justifyContent: 'right',
                            order: 3,
                            textAlign: 'right'
                        }}>
                            {acceptanceDoc && (
                                <Tooltip text={`Download ${acceptanceDoc.label} Document`} sx={{ mr: '12px', zIndex: 65, pt: '1px' }}>
                                    <ThemeLink
                                        as="a"
                                        variant="icon"
                                        href={acceptanceDoc.url}
                                    >
                                        <Icon icon="download" color="dark" />
                                    </ThemeLink>
                                </Tooltip>
                            )}
                            <StatusIcon size="15px" sx={{ textAlign: 'right', zIndex: 65 }} label={investment?.status?.name} status={investment?.status?.id} />
                        </Box>
                        {getRowLink(investment)}
                    </Grid>
                )
            }
        },
        {
            key: 'amount_invested',
            title: 'Amount invested',
            width: '144px',
            align: 'right',
            render: investment => investment?.capital && <Text as="p" variant="h5" color="darker" mb={1}>{formatCurrency(investment.capital)}*</Text>
        },
        {
            key: 'avg_price',
            title: 'Offer Price',
            align: 'right',
            render: investment => investment?.deal?.offer_price && <Text as="p" variant="h6" color="darker">{formatCurrency(investment.deal?.offer_price, investment.deal?.offer_price < 0.1 ? 4 : 3)}</Text>
        },
        {
            key: 'shares',
            title: 'Shares',
            align: 'right',
            render: investment => <Text as="p" variant="h6" color="darker">{investment?.deal_fulfilled ? (<>{investment?.shares}</>) : <>Not allocated</>}</Text>
        },
        {
            key: 'arrow',
            title: '',
            align: 'right',
            render: i => <Icon icon="arrow-long" className="table-row__arrow" />,
            sx: {
                flex: 1,
                justifyContent: 'right'
            }
        }
    ];

    return (
        <>
            <Flex mt={4} sx={{
                alignItems: 'center'
            }}>
                <Heading as="h3" variant="h3" sx={{ fontSize: '22px' }} mr={3} id="listedDeals" ref={headingRef}>{t('Deal History')}</Heading>
                {dealsToShow?.results.length > 0 ? (
                    <>
                        <Icon color="tealDark" icon="tag" size="14px" mr={2} />
                        <Text as="p" color="tealDark" variant="h5">{dealsToShow?.meta?.total} {dealsToShow?.meta?.total === 1 ? 'deal' : 'deals'}</Text>
                    </>
                ) : null}
            </Flex>

            <Table
                data={dealsToShow.results}
                columns={columns}
                prefix={`listed_table_${activePage}`}
                isLoading={isLoading}
                rowLink={row => getRowLink(row, { tabIndex: '-1' })}
                rowProps={() => ({
                    sx: {
                        textDecoration: 'none',
                        color: 'darker',
                        '.table-row__arrow': {
                            transform: 'translateX(-4px)',
                            transition: 'transform 0.3s ease-in-out'
                        },
                        '&:hover .table-row__arrow': {
                            transform: 'translateX(0)',
                            color: 'tealDark'
                        }
                    }
                })}
                my={3}
                minWidth={'1200px'}
                emptyTemplate={{
                    icon: 'portfolio',
                    heading: 'Invest in a deal on Liquidity',
                    // content: 'Start investing',
                    button: {
                        title: 'See deals',
                        to: '/deals'
                    }
                }}
            />

            <Pagination
                sx={{ my: 3 }}
                activePage={activePage ?? 0}
                pageCount={pageCount ?? 0}
                onPageChange={(event) => {
                    const payload = getPortfolioDeals({
                        params: {
                            profile: localStorage.getItem('activeProfile') ?? null,
                            listed: 1,
                            page: (event.selected + 1)
                        }
                    });
                    smoothScroll(headingRef);
                    setLoading(true);
                    dispatch(getPortfolioListedDealsAsync(payload))
                }}
            />
        </>
    )
};
